import type { CityConfig } from "../city-template/types";

export const florenceConfig: CityConfig = {
  city: "Florence",
  state: "Kentucky",
  stateAbbr: "KY",
  slug: "florence",
  route: "/cities/florence",
  title: "Emergency Passport Florence, KY | Fast Passport Renewal | Boone County",
  metaDescription: "Emergency passport and expedited passport services in Florence, KY — as fast as 24 hours. Fast passport renewal, same-day help, and child passport expediting for Boone County, Northern Kentucky, and CVG airport travelers. A+ BBB rated, registered State Dept. courier.",
  h1: "Emergency & Expedited Passport Services in Florence, KY",
  heroSubtitle: "Passport expired before your trip? Need an emergency passport fast? We help Florence and Northern Kentucky travelers get their expedited passports as quickly as 24 hours. Serving Boone County, the CVG airport corridor, and the Northern Kentucky retail hub. A+ BBB rated. No office visit required.",
  heroImageUrl: "https://readdy.ai/api/search-image?query=Florence%20Kentucky%20suburban%20cityscape%20in%20Boone%20County%20with%20modern%20retail%20corridor%20and%20rolling%20green%20hills%2C%20clean%20suburban%20development%20with%20warm%20Kentucky%20sunset%20light%2C%20shopping%20and%20residential%20areas%20with%20trees%20and%20green%20space%2C%20clean%20panoramic%20photography%2C%20no%20visible%20text%20or%20people%2C%20Northern%20Kentucky%20suburban%20landscape%20with%20commercial%20buildings%20and%20natural%20surroundings%2C%20wide%20panoramic%20view&width=1800&height=700&seq=florence-hero-001&orientation=landscape",
  heroImageAlt: "Florence Kentucky suburban cityscape in Boone County with modern retail corridor and rolling green hills",
  geoPosition: "38.9989;-84.6266",
  geoPlacename: "Florence, Kentucky",
  regionalAgency: {
    name: "Chicago Passport Agency",
    address: "77 W Jackson Blvd, Suite 1200, Chicago, IL 60604",
    distance: "Located in downtown Chicago — approximately 310 miles from Florence via I-74 and I-65",
  },
  nearbyCitiesZips: [
    { name: "Florence", zips: "41022, 41042, 41043, 41044" },
    { name: "Erlanger", zips: "41018, 41017, 41051" },
    { name: "Union", zips: "41091, 41042" },
    { name: "Hebron", zips: "41048, 41005, 41018, 41094, 41099" },
    { name: "Burlington", zips: "41005, 41042, 41048, 41094, 41099" },
    { name: "Independence", zips: "41051" },
    { name: "Fort Mitchell", zips: "41011, 41017, 41051" },
    { name: "Walton", zips: "41094, 41091" },
    { name: "Villa Hills", zips: "41017" },
    { name: "Crestview Hills", zips: "41017" },
    { name: "Edgewood", zips: "41017, 41018, 41051" },
    { name: "Taylor Mill", zips: "41015, 41017" },
  ],
  benefits: [
    { icon: "ri-time-line", title: "24–48 Hour Processing", desc: "Emergency courier submission for urgent Boone County departures" },
    { icon: "ri-shield-check-line", title: "Registered Courier", desc: "U.S. Department of State approved passport expediting" },
    { icon: "ri-user-line", title: "Dedicated Agent", desc: "A real person assigned to your case from start to finish" },
    { icon: "ri-mail-send-line", title: "Document Shipping", desc: "Prepaid overnight labels to our processing center" },
  ],
  internalLinks: [
    { name: "Expedited passport services in Covington", route: "/cities/covington" },
    { name: "Fast passport renewal in Louisville", route: "/cities/louisville" },
    { name: "Emergency passport help in Lexington", route: "/cities/lexington" },
    { name: "Urgent passport services in Bowling Green", route: "/cities/bowling-green" },
    { name: "Same-day passport help in Erlanger", route: "/cities/erlanger" },
    { name: "Passport courier services near Union", route: "/union" },
  ],
  fpcOfficeNote: "Fast Passport Center serves Florence remotely via secure courier. We do not have a walk-in office in Florence — our registered couriers hand-deliver your documents directly to U.S. Department of State processing facilities. This is how we achieve 24–48 hour turnaround for Boone County travelers.",
  fpcOfficeDistance: "Serving all of Boone County and the Northern Kentucky CVG corridor",
  serviceAreaIntro: "From the Florence retail corridor to the Union area, from the CVG airport corridor to the Hebron and Burlington communities — Fast Passport Center serves the entire Boone County region. We regularly help travelers from Erlanger, Independence, and Fort Mitchell get their passports fast.",
  nearbyCitiesIntro: "We serve all Florence neighborhoods and nearby Boone County cities including Erlanger, Union, Hebron, Burlington, Independence, Fort Mitchell, Walton, Villa Hills, Crestview Hills, Edgewood, and Taylor Mill. If you are in Boone County, Northern Kentucky, or the CVG airport corridor, we have you covered.",
  howItWorksIntro: "Most Florence travelers complete everything online and ship documents overnight. No need to drive to Chicago or wait in line at the regional agency.",
  mistakesIntro: "Florence travelers often face long drives to the nearest regional passport agency. Here are the most common mistakes that cause delays.",
  testimonialsIntro: "Florence and Northern Kentucky travelers trust us when their departure is days away. Here is what some of them have to say.",
  faqIntro: "Have questions about expedited passports in Florence? Here are the most common ones we hear from Boone County travelers.",
  statePageRoute: "/states/kentucky",
  statePageName: "Kentucky",
  stickyNavLinks: [
    { label: "Processing", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Service Area", href: "#service-area" },
    { label: "Facilities", href: "#acceptance-facilities" },
    { label: "Comparison", href: "#comparison" },
    { label: "How It Works", href: "#how-it-works" },
    { label: "FAQ", href: "#faq" },
    { label: "Resources", href: "#local-resources" },
  ],
  processingOptions: [
    {
      speed: "24",
      unit: "Hours*",
      label: "Regional Agency Processing",
      tag: "Emergency Only",
      tagColor: "bg-gray-100 text-gray-500",
      features: [
        "Appointment required — no walk-ins allowed",
        "Life-or-death emergencies only",
        "Bereavement or medical crisis",
        "Expect to wait all day or return next day",
      ],
      highlight: false,
      emergency: true,
    },
    {
      speed: "3–9",
      unit: "Business Days",
      label: "Fast Passport Center",
      tag: "Most Popular",
      tagColor: "bg-[#F6C344] text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "Reserved agency submission",
        "Predictable turnaround",
        "Ideal for upcoming trips",
        "Document review included",
        "Dedicated agent assigned to your case",
        "Daily status updates — never left guessing",
        "Not affected by government shutdowns",
      ],
      highlight: true,
    },
    {
      speed: "3–5",
      unit: "Weeks",
      label: "Fast Passport Center",
      tag: "Standard Expedited",
      tagColor: "bg-[#1A365D]/10 text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "Reserved agency submission",
        "Faster than self-service",
        "Good for planned travel",
        "Document review included",
        "Dedicated agent assigned to your case",
        "Daily status updates — never left guessing",
        "Not affected by government shutdowns",
      ],
      highlight: false,
      smartSaver: true,
    },
    {
      speed: "1–3",
      unit: "Months",
      label: "Self-Service (Post Office)",
      tag: "Slowest",
      tagColor: "bg-gray-100 text-gray-400",
      features: [
        "No courier access — standard mail only",
        "Workload & wait times shift with shutdowns and budget fights",
        "In-person clerk appointment required",
        "No document review — errors cause rejection",
        "No dedicated agent — you are on your own",
        "Updates only by physical mail letter",
        "No phone or email contact for your case",
        "Can't upgrade speed after submission",
        "Not suitable if travel is within 10 weeks",
      ],
      highlight: false,
      muted: true,
    },
  ],
  services: [
    {
      icon: "ri-flashlight-line",
      title: "Emergency Passport",
      description: "For travelers with immediate departure needs — as fast as 24 hours for urgent Boone County international flights.",
      bullets: ["24–48 hour turnaround available", "Registered courier hand-delivery", "Flight verification support"],
      badge: "Most Popular",
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-passport-line",
      title: "Passport Renewal",
      description: "Fast renewal for adults with a passport issued within the last 15 years at age 16 or older.",
      bullets: ["No clerk visit required for most", "Mail-in or courier options", "Lost passport replacement available"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-add-circle-line",
      title: "New Passport",
      description: "First-time passport application guidance with acceptance clerk appointment support.",
      bullets: ["Acceptance facility locator", "Form DS-11 assistance", "Photo requirement guidance"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-alert-line",
      title: "Lost or Stolen Passport",
      description: "Passport replacement with DS-64 form guidance and police report documentation.",
      bullets: ["DS-64 form assistance", "Identity verification support", "Emergency replacement options"],
      badge: "Urgent",
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport",
      description: "Passport services for minors under 16 with parental consent and acceptance clerk support.",
      bullets: ["DS-11 form for minors", "Parental consent guidance", "Both parents presence rules"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-file-edit-line",
      title: "Name Change",
      description: "Passport correction and name change service after marriage, divorce, or legal changes.",
      bullets: ["DS-5504 or DS-82 guidance", "Marriage certificate support", "Court order documentation"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport", description: "Urgent passport processing for travelers with immediate departure needs, as fast as 24 hours." },
    { name: "Passport Renewal", description: "Fast passport renewal for U.S. citizens with a passport issued within the last 15 years at age 16 or older." },
    { name: "New Passport", description: "First-time passport application assistance with step-by-step guidance through the acceptance clerk process." },
    { name: "Lost or Stolen Passport", description: "Passport replacement service for lost or stolen passports, including DS-64 form guidance." },
    { name: "Child Passport", description: "Passport services for minors under 16, with parental consent guidance and acceptance clerk support." },
    { name: "Name Change", description: "Passport correction and name change service after marriage, divorce, or legal name changes." },
  ],
  testimonials: [
    {
      name: "Nicole A.",
      location: "Florence, KY",
      rating: 5,
      text: "My husband travels for business through CVG constantly and his passport expired before a trip to Frankfurt. Fast Passport Center renewed it in 48 hours. They know how important timing is for airport corridor residents.",
      service: "Passport Renewal",
    },
    {
      name: "Steven M.",
      location: "Union, KY",
      rating: 5,
      text: "We booked a Disney cruise for the kids and realized my daughter's passport had expired 3 weeks before sailing. They expedited her child passport in 4 days. The kids got to meet Mickey on Castaway Cay.",
      service: "Child Passport",
    },
    {
      name: "Rachel K.",
      location: "Hebron, KY",
      rating: 5,
      text: "Emergency family situation in Ireland required travel within 72 hours. Fast Passport Center processed my emergency passport in 24 hours. Living near CVG, I was on a plane the next day. Incredible service.",
      service: "Emergency Passport",
    },
    {
      name: "Daniel P.",
      location: "Erlanger, KY",
      rating: 5,
      text: "Needed a new passport for a study abroad semester in Italy. I was worried about the paperwork but they walked me through every step. Passport arrived in 6 days. Ready for my semester in Florence, Italy.",
      service: "New Passport",
    },
  ],
  facilities: [
    {
      id: "florence-1",
      name: "Florence Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "7100 Turfway Rd",
      city: "Florence",
      state: "KY",
      zip: "41042",
      phone: "(859) 371-1511",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "0.3 miles from the Florence retail corridor",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Major facility for Boone County. Appointments required. Call ahead to confirm passport acceptance times.",
      icon: "ri-mail-line",
    },
    {
      id: "florence-2",
      name: "Erlanger Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "3130 Dixie Hwy",
      city: "Erlanger",
      state: "KY",
      zip: "41018",
      phone: "(859) 727-1511",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "0.2 miles from Erlanger center",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Convenient for Kenton County residents. Appointment strongly recommended.",
      icon: "ri-mail-line",
    },
    {
      id: "florence-3",
      name: "Boone County Clerk",
      type: "County Clerk Passport Acceptance",
      address: "2950 Washington St",
      city: "Burlington",
      state: "KY",
      zip: "41005",
      phone: "(859) 334-2108",
      hours: "Mon–Fri: 8:00 AM – 4:30 PM",
      distance: "0.1 miles from Boone County Courthouse",
      website: "https://www.boonecountyky.org/",
      websiteLabel: "Boone County Clerk",
      notes: "Major county facility with extended hours. Accepts walk-ins but appointments recommended for faster service.",
      icon: "ri-government-line",
    },
    {
      id: "florence-4",
      name: "Union Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "10040 Usher Rd",
      city: "Union",
      state: "KY",
      zip: "41091",
      phone: "(859) 384-1511",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "0.3 miles from Union center",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Serves western Boone County. Photo services available. Book ahead.",
      icon: "ri-mail-line",
    },
    {
      id: "florence-5",
      name: "Hebron Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "2135 Petersburg Rd",
      city: "Hebron",
      state: "KY",
      zip: "41048",
      phone: "(859) 586-1511",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "0.2 miles from Hebron center",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Convenient for CVG airport corridor residents. Full passport services available.",
      icon: "ri-mail-line",
    },
    {
      id: "florence-6",
      name: "Fort Mitchell Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "3400 Highland Ave",
      city: "Fort Mitchell",
      state: "KY",
      zip: "41017",
      phone: "(859) 331-1511",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "0.2 miles from Fort Mitchell center",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Serves Kenton County residents. Convenient for Fort Mitchell and nearby communities.",
      icon: "ri-mail-line",
    },
  ],
  comparisonRows: [
    { feature: "Appointment Needed", agency: "Yes — call NPIC, limited slots", fpc: "No — we handle everything for you" },
    { feature: "Processing Speed", agency: "Same-day if approved (rare)", fpc: "24–48 hours guaranteed for emergency" },
    { feature: "Document Review", agency: "No — rejected if wrong", fpc: "Yes — we check before submission" },
    { feature: "Travel to Agency", agency: "Yes — 5 hour drive to Chicago", fpc: "No — ship from home or office" },
    { feature: "Agent Support", agency: "No — government clerk only", fpc: "Yes — dedicated agent assigned" },
    { feature: "Status Updates", agency: "No — no tracking provided", fpc: "Yes — daily email updates" },
    { feature: "Guaranteed Timing", agency: "No — subject to availability", fpc: "Yes — contractual turnaround times" },
  ],
  mistakes: [
    {
      icon: "ri-image-line",
      title: "Wrong Photo Size or Background",
      description: "Boone County photo shops often use generic sizes. Passport photos must be exactly 2×2 inches with a white or off-white background.",
    },
    {
      icon: "ri-file-list-line",
      title: "Incomplete DS-11 Form",
      description: "Missing signatures or using the wrong form type are the #1 reasons for rejection at Boone County acceptance facilities.",
    },
    {
      icon: "ri-money-dollar-circle-line",
      title: "Wrong Payment Amount",
      description: "Fees change frequently. Many Florence applicants show up with incorrect checks and have to reschedule their appointment.",
    },
    {
      icon: "ri-calendar-close-line",
      title: "No Proof of Travel",
      description: "Without flight confirmation or travel itinerary, the Chicago regional agency will turn you away for emergency appointments.",
    },
    {
      icon: "ri-parent-line",
      title: "Missing Parental Consent",
      description: "For minors, both parents must appear or submit notarized consent. This trips up many Boone County families at the clerk.",
    },
    {
      icon: "ri-time-line",
      title: "Waiting Too Long to Start",
      description: "Northern Kentucky travelers often assume standard processing will be fine. It takes 8–11 weeks. Start expedited at least 3 weeks before travel.",
    },
  ],
  steps: [
    { number: "1", icon: "ri-file-list-line", title: "Apply Online", description: "Complete your application on our secure website in under 10 minutes." },
    { number: "2", icon: "ri-mail-send-line", title: "Ship Documents", description: "We send you a prepaid overnight label. Drop your documents at any UPS location in Florence." },
    { number: "3", icon: "ri-shield-check-line", title: "We Review", description: "Our team checks every document for accuracy before hand-delivering to the State Department." },
    { number: "4", icon: "ri-passport-line", title: "Processing", description: "Your passport is processed at a U.S. Department of State facility using our registered courier slots." },
    { number: "5", icon: "ri-truck-line", title: "Delivery", description: "We overnight your new passport back to your Florence address — trackable every step of the way." },
  ],
  faqs: [
    { q: "Can I get an emergency passport in Florence before international travel?", a: "Yes. Fast Passport Center provides emergency passport services in Florence for travelers with urgent international flights. Our registered couriers hand-deliver your documents directly to U.S. Department of State facilities, achieving 24–48 hour turnaround — without the 5 hour drive to Chicago." },
    { q: "How fast can I renew my passport in Florence?", a: "Fast Passport Center offers fast passport renewal in Florence in as little as 24–48 hours through our emergency courier service. For less urgent travel, our 3–5 day expedited option provides the best value. Both options include full document review and a dedicated agent." },
    { q: "What if my passport expires before my cruise?", a: "If your passport expires before your cruise, contact Fast Passport Center immediately. Many cruise lines require your passport to be valid for at least 6 months beyond your travel date. We can expedite your passport renewal in Florence in 24–48 hours so you don't miss your departure." },
    { q: "Can I get a same-day passport appointment near Florence?", a: "Same-day passport appointments are only available at the Chicago Regional Passport Agency — a 5 hour drive from Florence — for documented life-or-death emergencies. For all other urgent travel, Fast Passport Center's 24–48 hour emergency courier processing is the fastest guaranteed option for Boone County residents." },
    { q: "Can I expedite a child passport in Florence?", a: "Yes. Fast Passport Center expedites child passports in Florence for minors under 16. Both parents must appear or submit notarized consent, along with the child's birth certificate and both parents' IDs. We walk Boone County families through the entire process." },
    { q: "What should I do if I lost my passport before a trip?", a: "If you lost your passport before a trip, contact Fast Passport Center immediately. We provide lost passport replacement services in Florence with DS-64 form guidance and identity verification support. Our emergency courier service can get your replacement passport in 24–48 hours." },
    { q: "How fast can I get a passport in Florence?", a: "With Fast Passport Center's emergency courier service, Florence travelers can receive their passport in as little as 24–48 hours. Our registered couriers hand-deliver your documents directly to U.S. Department of State processing facilities, bypassing standard mail delays." },
    { q: "Do I need to drive to the Chicago Passport Agency?", a: "No. With Fast Passport Center, you complete everything online and ship your documents overnight. Our couriers handle the in-person delivery to State Department facilities. You only need to visit a local acceptance clerk if you're a first-time applicant or need a name change." },
    { q: "Where are the closest passport acceptance facilities in Florence?", a: "Popular options include the Florence Post Office (41042), Erlanger Post Office (41018), Boone County Clerk (41005), Union Post Office (41091), Hebron Post Office (41048), and Fort Mitchell Post Office (41017). All require appointments. We help you find the most convenient location and schedule your visit." },
    { q: "Can I get a same-day passport in Florence?", a: "Same-day passports are only available at the Chicago Regional Passport Agency for documented life-or-death emergencies with proof of travel within 72 hours. For all other urgent travel, Fast Passport Center's 24–48 hour emergency processing is the fastest reliable option — without the 5 hour drive to Chicago." },
    { q: "What if I live near the CVG airport?", a: "We serve the entire CVG airport corridor including Hebron, Burlington, Union, Erlanger, and Independence. Our overnight shipping labels work from any UPS location, so your proximity to the airport doesn't affect processing speed — in fact, it makes international travel even easier once your passport arrives." },
    { q: "How much does expedited passport service cost in Florence?", a: "Government fees are fixed: $130 for a passport book (adult) plus a $60 expedite fee. Fast Passport Center's service fees vary by processing speed — emergency 24–48 hour processing, 3–5 day expedited, or standard options. Contact us for a personalized quote based on your timeline." },
    { q: "Do you help with passport photos in Florence?", a: "We guide you on exact photo requirements and can recommend locations near you. Many CVS, Walgreens, and FedEx locations in Florence, Erlanger, and Hebron offer passport photo services. We review your photos before submission to ensure they meet State Department standards." },
    { q: "What documents do I need for a first-time passport in Florence?", a: "You'll need proof of citizenship (birth certificate or naturalization certificate), government-issued photo ID (driver's license or REAL ID), two passport photos, and payment. We provide a complete checklist and review your documents before you visit the acceptance clerk." },
    { q: "Can you help with a child's passport in Florence?", a: "Yes. Child passports require both parents (or notarized consent from the absent parent), the child's birth certificate, and both parents' IDs. We walk Boone County families through the entire process and help schedule acceptance clerk appointments." },
  ],
  photoLocations: [
    { name: "CVS Pharmacy — Florence", address: "6900 Burlington Pike, Florence, KY 41042", hours: "Mon–Sun: 8AM–10PM" },
    { name: "Walgreens — Florence", address: "7200 Mall Rd, Florence, KY 41042", hours: "Mon–Sun: 7AM–11PM" },
    { name: "Walmart — Florence", address: "7625 Doering Dr, Florence, KY 41042", hours: "Mon–Sun: 6AM–11PM" },
    { name: "Rite Aid — Florence", address: "8050 Usher Rd, Florence, KY 41042", hours: "Mon–Sun: 8AM–10PM" },
    { name: "CVS Pharmacy — Erlanger", address: "3415 Dixie Hwy, Erlanger, KY 41018", hours: "Mon–Sun: 8AM–10PM" },
    { name: "Walmart — Erlanger", address: "3100 Dixie Hwy, Erlanger, KY 41018", hours: "Mon–Sun: 6AM–11PM" },
    { name: "Walgreens — Union", address: "10054 Usher Rd, Union, KY 41091", hours: "Mon–Sun: 7AM–11PM" },
    { name: "FedEx Office — Florence", address: "4990 Houston Rd, Florence, KY 41042", hours: "Mon–Fri: 7AM–9PM, Sat: 8AM–6PM" },
  ],
  dmvLocations: [
    {
      name: "Circuit Court Clerk — Boone County",
      address: "2950 Washington St, Burlington, KY 41005",
      hours: "Mon–Fri: 8AM–4:30PM",
      note: "Driver's license and REAL ID. Appointment recommended.",
      featured: true,
    },
    {
      name: "Circuit Court Clerk — Kenton County",
      address: "303 Court St, Covington, KY 41011",
      hours: "Mon–Fri: 8AM–4:30PM",
      note: "REAL ID and driver's license renewals. Online booking available.",
      featured: false,
    },
    {
      name: "Circuit Court Clerk — Campbell County",
      address: "330 York St, Newport, KY 41071",
      hours: "Mon–Fri: 8AM–4:30PM",
      note: "Full-service center for Campbell County residents.",
      featured: true,
    },
    {
      name: "Ohio BMV — Cincinnati",
      address: "5721 Dragon Way, Cincinnati, OH 45227",
      hours: "Mon–Fri: 8AM–5PM",
      note: "Alternative for Ohio residents across the river. Walk-ins limited.",
      featured: false,
    },
  ],
  airports: [
    {
      code: "CVG",
      name: "Cincinnati/Northern Kentucky International",
      distance: "8 miles from Florence",
      address: "3087 Terminal Dr, Hebron, KY 41048",
      note: "The primary airport for Boone County and the Northern Kentucky region. Domestic and international flights to major hubs worldwide.",
      flightUrl: "https://www.cvgairport.com/",
      flightLabel: "CVG Airport",
      featured: true,
    },
    {
      code: "SDF",
      name: "Louisville Muhammad Ali International",
      distance: "110 miles from Florence",
      address: "600 Terminal Dr, Louisville, KY 40209",
      note: "Alternative regional airport with flights to major hubs.",
      flightUrl: "https://www.flylouisville.com/",
      flightLabel: "Louisville Airport",
      featured: false,
    },
    {
      code: "LEX",
      name: "Blue Grass Airport (Lexington)",
      distance: "75 miles from Florence",
      address: "4000 Terminal Dr, Lexington, KY 40510",
      note: "Alternative regional airport for central Kentucky flights.",
      flightUrl: "https://www.bluegrassairport.com/",
      flightLabel: "Blue Grass Airport",
      featured: false,
    },
  ],
};